'use client'

import { Button } from "@/components/ui/button"
import { Check, Crown, ArrowRight } from "lucide-react"
import { useRouter } from "next/navigation"
import { useLanguage } from "@/lib/i18n/language-provider"
import { ScrollReveal } from "@/components/scroll-reveal"
import { captureEvent } from "@/lib/posthog-helper"

export function Pricing() {
  const router = useRouter()
  const { t } = useLanguage()

  const handlePricingCTAClick = () => {
    captureEvent('signup_flow:pricing_cta_click', {
      button_location: 'landing_pricing_section',
      destination: '/pro',
    })
    router.push('/pro')
  }

  const features = [
    t('landing.pricing.allFeatures'),
    t('landing.pricing.fullSynoptic'),
    t('landing.pricing.unlimitedMultiGrid'),
    t('paywall.prioritySupport'),
  ]

  return (
    <section className="py-16 md:py-32 relative overflow-hidden">
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff12_2px,transparent_2px),linear-gradient(to_bottom,#ffffff12_2px,transparent_2px)] bg-[size:80px_80px]" />

      <div className="w-full relative z-10 px-4 md:px-6 flex flex-col items-center">
        <div className="max-w-5xl w-full">
          <ScrollReveal animation="slide-down" duration={1}>
            <h2 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold tracking-tight text-center mb-12 md:mb-16">
              {t('landing.pricing.title')}
            </h2>
          </ScrollReveal>

          {/* Grid with List + CTA */}
          <ScrollReveal animation="fade-in" delay={0.2} duration={1} className="relative">
            <div className="absolute -inset-4 bg-foreground/5 blur-2xl" />
            <div className="relative border-2 md:border-4 border-foreground/20 bg-background p-6 sm:p-8 md:p-12 lg:p-16">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 items-center">
                {/* Left - Benefits list */}
                <div className="space-y-4 md:space-y-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 border border-foreground/20 text-xs sm:text-sm font-semibold mb-2 md:mb-4">
                    <Crown className="w-3 h-3 sm:w-4 sm:h-4" />
                    {t('landing.pricing.versionPro')}
                  </div>
                  {features.map((item, index) => (
                    <div key={index} className="flex items-start gap-3 md:gap-4">
                      <div className="w-5 h-5 md:w-6 md:h-6 border-2 border-foreground flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-1">
                        <Check className="h-3 w-3 md:h-4 md:w-4" />
                      </div>
                      <span className="text-sm sm:text-base md:text-lg">{item}</span>
                    </div>
                  ))}
                </div>

                {/* Right - Price and CTA */}
                <div className="flex flex-col items-center justify-center text-center space-y-6 md:space-y-8 border-t-2 md:border-t-0 md:border-l-2 border-foreground/20 pt-8 md:pt-0 md:pl-12">
                  <div>
                    <div className="text-5xl sm:text-6xl md:text-7xl font-bold mb-2">9,99€</div>
                    <div className="text-base md:text-lg text-muted-foreground">{t('paywall.perMonth')}</div>
                  </div>

                  <Button
                    size="lg"
                    onClick={handlePricingCTAClick}
                    className="w-full text-base md:text-lg px-6 md:px-8 py-5 md:py-6 h-auto bg-foreground text-background hover:bg-foreground/90 group"
                  >
                    {t('account.upgradeToPro')}
                    <ArrowRight className="ml-2 h-4 w-4 md:h-5 md:w-5 transition-transform group-hover:translate-x-1" />
                  </Button>

                  <p className="text-xs md:text-sm text-muted-foreground px-4">
                    {t('landing.pricing.subtitle')}
                  </p>
                </div>
              </div>
            </div>
          </ScrollReveal>
        </div>
      </div>
    </section>
  )
}

